import type { ElementType } from "react"
import { useTranslations } from "use-intl"
import { InputOTP, InputOTPGroup, InputOTPSlot } from "./InputOTP"

const CODE_LENGTH = 6

/**
 * The three parts of a one-time-code field (`InputOTP` taking `maxLength`/`value`/`onChange`, a group,
 * and a slot taking an `index`); an app whose look differs passes its own.
 */
export interface OtpComponents {
  InputOTP: ElementType
  InputOTPGroup: ElementType
  InputOTPSlot: ElementType
}

const DEFAULT_COMPONENTS: OtpComponents = { InputOTP, InputOTPGroup, InputOTPSlot }

/**
 * The code step of an invitation, shared by both apps so the wording and the shape of the field are
 * defined once (`auth.invite` in the catalog): where the code was sent, whom it is to join, how long
 * it lasts, and the six-digit field.
 */
export function InviteCodeEntry({
  email,
  companyName,
  code,
  onCodeChange,
  components = DEFAULT_COMPONENTS,
}: {
  email: string
  companyName: string
  code: string
  onCodeChange: (code: string) => void
  components?: OtpComponents
}) {
  const t = useTranslations("auth.invite")
  const { InputOTP: Otp, InputOTPGroup: Group, InputOTPSlot: Slot } = components

  return (
    <>
      <div>
        <p className="text-sm text-muted-foreground">
          {t.rich("codeSent", {
            email,
            companyName,
            highlight: (chunks) => <span className="text-foreground">{chunks}</span>,
          })}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">{t("codeExpiry")}</p>
      </div>

      <Otp maxLength={CODE_LENGTH} value={code} onChange={onCodeChange}>
        <Group>
          {Array.from({ length: CODE_LENGTH }).map((_, index) => (
            <Slot key={index} index={index} />
          ))}
        </Group>
      </Otp>
    </>
  )
}
